import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { uploadFile } from '../../lib/files.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { Spinner } from '../ui.js'
import { FieldLayer, dockPrimaryClass } from './kit.js'

/**
 * What a technician adds to a job from where they stand: a photo, a video, a
 * note or a voice clip. Every one of them is an ordinary job file or note, so
 * the office sees it on the full card the moment it lands.
 *
 * Held once per open job and handed to both action rows (the sheet's and the
 * feed's), so a voice clip keeps recording while the feed is opened.
 */
export function useCapture(jobId: string) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const cameraInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(0)
  const [failure, setFailure] = useState<string | null>(null)

  const landed = useCallback(() => {
    void queryClient.invalidateQueries(trpc.jobs.byId.queryFilter({ id: jobId }))
  }, [queryClient, trpc, jobId])

  const upload = useCallback(
    async (files: File[], kind?: 'AUDIO') => {
      if (files.length === 0) return
      setFailure(null)
      setUploading((left) => left + files.length)
      try {
        for (const file of files) {
          await uploadFile(file, { jobId, kind: kind ?? mediaKind(file) })
          setUploading((left) => left - 1)
        }
      } catch (cause) {
        setFailure(cause instanceof Error ? cause.message : 'That did not upload.')
      } finally {
        setUploading(0)
        landed()
      }
    },
    [jobId, landed],
  )

  const voice = useVoiceClip((file) => void upload([file], 'AUDIO'), setFailure)

  // No `capture` attribute on purpose: without it iOS offers its own menu,
  // Take Photo or Video (the camera, with its Photo / Video switch), Photo
  // Library and Choose File. With it, the library is unreachable.
  const inputs = (
    <input
      ref={cameraInput}
      type="file"
      accept="image/*,video/mp4,video/quicktime"
      multiple
      hidden
      data-testid="camera-input"
      onChange={(event) => {
        void upload(Array.from(event.target.files ?? []))
        event.target.value = ''
      }}
    />
  )

  return {
    inputs,
    uploading,
    failure,
    openCamera: () => cameraInput.current?.click(),
    voice,
  }
}

/** Photo or video, by what the picker handed back. */
function mediaKind(file: File): 'PHOTO' | 'VIDEO' {
  return file.type.startsWith('video/') || /\.(mov|mp4|m4v)$/i.test(file.name) ? 'VIDEO' : 'PHOTO'
}

export type Capture = ReturnType<typeof useCapture>

/**
 * A voice clip, recorded on the device and uploaded as an `AUDIO` file.
 *
 * Not dictation: nothing is transcribed and nothing is written into a field.
 * It is the office's to listen to, like a photo is theirs to look at.
 */
function useVoiceClip(onClip: (file: File) => void, onFailure: (message: string) => void) {
  const recorder = useRef<MediaRecorder | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [seconds, setSeconds] = useState(0)
  const clip = useRef(onClip)
  clip.current = onClip

  const supported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)

  useEffect(() => {
    if (startedAt === null) return
    const tick = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      500,
    )
    return () => clearInterval(tick)
  }, [startedAt])

  // Leaving the job mid-sentence keeps the clip rather than throwing it away.
  useEffect(() => () => recorder.current?.stop(), [])

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const media = new MediaRecorder(stream)
      const chunks: Blob[] = []
      media.ondataavailable = (event) => chunks.push(event.data)
      media.onstop = () => {
        stream.getTracks().forEach((track) => track.stop())
        recorder.current = null
        setStartedAt(null)
        setSeconds(0)
        // "audio/webm;codecs=opus" is not a type the file route accepts; the
        // part before the semicolon is.
        const type = (media.mimeType || 'audio/webm').split(';')[0]!
        const extension = type === 'audio/mp4' ? 'm4a' : (type.split('/')[1] ?? 'webm')
        const stamp = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
        clip.current(new File(chunks, `Voice note ${stamp}.${extension}`, { type }))
      }
      media.start()
      recorder.current = media
      setStartedAt(Date.now())
    } catch {
      onFailure('The microphone is off for this app. Allow it in Settings to record.')
    }
  }

  return {
    supported,
    recording: startedAt !== null,
    seconds,
    toggle: () => (recorder.current ? recorder.current.stop() : void start()),
  }
}

interface Action {
  label: string
  icon: IconName
  onClick: () => void
  recording?: boolean
  disabled?: boolean
  /** Camera is pressed most, so the bar draws it dark. */
  lead?: boolean
}

function actionsOf(
  capture: Capture,
  canCapture: boolean,
  onNote: () => void,
  onBilling: (() => void) | undefined,
): Action[] {
  const { voice } = capture
  const clock = `${Math.floor(voice.seconds / 60)}:${String(voice.seconds % 60).padStart(2, '0')}`
  return [
    ...(canCapture
      ? [
          { label: 'Camera', icon: 'camera' as const, onClick: capture.openCamera, lead: true },
          { label: 'Note', icon: 'note' as const, onClick: onNote },
          {
            label: voice.recording ? `Stop ${clock}` : 'Voice',
            icon: voice.recording ? ('stop' as const) : ('mic' as const),
            onClick: voice.toggle,
            recording: voice.recording,
            disabled: !voice.supported,
          },
        ]
      : []),
    ...(onBilling ? [{ label: 'Billing', icon: 'pricing' as const, onClick: onBilling }] : []),
  ]
}

interface ActionProps {
  capture: Capture
  canCapture: boolean
  onNote: () => void
  /** Absent when this person may not see the bill. */
  onBilling?: () => void
}

/**
 * The job's actions floating over the foot of the screen: Camera, Note,
 * Voice and Billing. The scroll area leaves room for it
 * (`captureBarRoom`), so the last card is never under the glass.
 */
export function CaptureBar({ capture, canCapture, onNote, onBilling }: ActionProps) {
  const actions = actionsOf(capture, canCapture, onNote, onBilling)
  if (actions.length === 0) return null

  return (
    <div className="pointer-events-none absolute inset-x-3.5 bottom-[calc(var(--safe-bottom)+16px)] z-10 mx-auto flex max-w-[560px] flex-col gap-2">
      <CaptureStatus capture={capture} floating />
      <div
        role="toolbar"
        aria-label="Add to the job"
        className="pointer-events-auto flex h-16 items-center gap-1.5 rounded-lg bg-white/65 px-2 shadow-[0_10px_30px_rgb(20_35_25_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.9)] backdrop-blur-2xl backdrop-saturate-[1.8]"
      >
        {actions.map((action) => (
          <button
            key={action.icon}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            aria-pressed={action.recording ?? undefined}
            className={cn(
              'flex h-12 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-md text-[10px] font-semibold tabular-nums transition-colors disabled:opacity-40',
              action.recording
                ? 'bg-red-600 text-white'
                : action.lead
                  ? 'bg-ink-900 text-brand-200 active:bg-ink-800'
                  : 'bg-ink-900/[0.06] text-ink-900 active:bg-ink-900/10',
            )}
          >
            <Icon name={action.icon} className="size-5" strokeWidth={1.8} />
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Bottom padding for a scroll area with the `CaptureBar` over it. */
export const captureBarRoom = 'pb-[calc(var(--safe-bottom)+104px)]'

/** The same actions as a row of tiles, at the foot of the feed. */
export function QuickActions({ capture, canCapture, onNote, onBilling }: ActionProps) {
  const actions = actionsOf(capture, canCapture, onNote, onBilling)
  if (actions.length === 0) return null

  return (
    <div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))` }}
      >
        {actions.map((action) => (
          <button
            key={action.icon}
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            aria-pressed={action.recording ?? undefined}
            className={cn(
              'flex h-[50px] flex-col items-center justify-center gap-0.5 rounded-md text-[11px] font-semibold tabular-nums ring-1 ring-inset transition-colors disabled:opacity-40',
              action.recording
                ? 'bg-red-50 text-red-700 ring-red-200'
                : 'bg-white text-ink-800 ring-ink-200 active:bg-ink-50',
            )}
          >
            <Icon name={action.icon} className="size-5" />
            {action.label}
          </button>
        ))}
      </div>
      <CaptureStatus capture={capture} />
    </div>
  )
}

function CaptureStatus({ capture, floating = false }: { capture: Capture; floating?: boolean }) {
  const shape = floating
    ? 'self-center rounded-md bg-white px-3.5 py-2 shadow-[0_6px_20px_rgb(20_35_25_/_0.14)]'
    : 'mt-1.5 px-1'
  if (capture.uploading > 0) {
    return (
      <p className={cn('flex items-center gap-1.5 text-[13px] text-ink-600', shape)}>
        <Spinner className="size-3.5 text-ink-500" />
        {capture.uploading === 1 ? 'Uploading…' : `Uploading ${capture.uploading}…`}
      </p>
    )
  }
  if (capture.failure) {
    return (
      <p role="alert" className={cn('text-[13px] text-red-700', shape)}>
        {capture.failure}
      </p>
    )
  }
  return null
}

/** A note on the job, typed at the door. The same note the office writes. */
export function NoteSheet({
  jobId,
  subtitle,
  onClose,
}: {
  jobId: string
  subtitle: string
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')

  const save = useMutation(
    trpc.jobs.addNote.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.byId.queryFilter({ id: jobId }))
        onClose()
      },
    }),
  )

  return (
    <FieldLayer
      title="Add a note"
      subtitle={subtitle}
      size="auto"
      onClose={onClose}
      footer={
        <button
          type="button"
          disabled={body.trim() === '' || save.isPending}
          onClick={() => save.mutate({ jobId, body: body.trim() })}
          className={cn(dockPrimaryClass, 'flex-1')}
        >
          {save.isPending ? <Spinner className="size-5" /> : null}
          Save note
        </button>
      }
    >
      <div className="flex flex-col gap-2 px-4 py-4">
        <textarea
          aria-label="Note"
          autoFocus
          rows={5}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="What you found, what you did, what the client said"
          className="w-full resize-y rounded-md bg-white px-3.5 py-3 text-base leading-relaxed text-ink-900 ring-1 ring-ink-200 outline-none ring-inset placeholder:text-ink-400 focus:ring-brand-500"
        />
        {save.error ? (
          <p role="alert" className="px-1 text-[13px] text-red-700">
            {errorMessage(save.error)}
          </p>
        ) : null}
      </div>
    </FieldLayer>
  )
}

export function SectionLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[14px] font-semibold tracking-normal text-brand-700 normal-case"
    >
      {children}
    </button>
  )
}
